import React, { useState, useEffect } from 'react';
import { Modal, Card, Statistic, Row, Col, Spin, message } from 'antd';
import {
  MessageOutlined,
  CommentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { getWidgetAnalytics, WidgetAnalytics } from '../../lib/widgetsApi';

interface WidgetAnalyticsModalProps {
  visible: boolean;
  widgetId: string;
  onClose: () => void;
}

const WidgetAnalyticsModal: React.FC<WidgetAnalyticsModalProps> = ({
  visible,
  widgetId,
  onClose,
}) => {
  const [analytics, setAnalytics] = useState<WidgetAnalytics | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && widgetId) {
      loadAnalytics();
    }
  }, [visible, widgetId]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const data = await getWidgetAnalytics(widgetId);
      setAnalytics(data);
    } catch (error: any) {
      message.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Widget Analytics"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      {loading ? (
        <div className="text-center py-12">
          <Spin size="large" />
        </div>
      ) : analytics ? (
        <div>
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-1">{analytics.widgetName}</h3>
            <p className="text-gray-500 text-sm">Widget ID: {analytics.widgetId}</p>
          </div>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Total Conversations"
                  value={analytics.totalConversations}
                  prefix={<CommentOutlined />}
                  valueStyle={{ color: '#13afe3' }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Total Messages"
                  value={analytics.totalMessages}
                  prefix={<MessageOutlined />}
                  valueStyle={{ color: '#6366f1' }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Active Conversations"
                  value={analytics.activeConversations}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#10B981' }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Avg Messages/Conversation"
                  value={analytics.averageMessagesPerConversation}
                  precision={1}
                  prefix={<MessageOutlined />}
                  valueStyle={{ color: '#F59E0B' }}
                />
              </Card>
            </Col>
          </Row>

          {analytics.lastUsedAt && (
            <div className="mt-4 p-4 bg-gray-50 rounded">
              <div className="flex items-center text-sm text-gray-600">
                <ClockCircleOutlined className="mr-2" />
                <span>
                  Last used: {new Date(analytics.lastUsedAt).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No analytics available
        </div>
      )}
    </Modal>
  );
};

export default WidgetAnalyticsModal;
